/**
 * Undo/redo helpers for nested campaigns created by paste.
 *
 * Paste creates real campaign rows via onAddSprint. Undo must trash them
 * (not just remove the parent mission cards), or they linger in the sidebar.
 */

import { sortNestedSprintsForPersistence } from '../../../FlowchartView/hooks/clipboardUtils/nestedSprintPersistence';

/**
 * Snapshot pasted nested sprints and identify roots (direct children of the
 * paste target). Trashing roots cascade-trashes their descendants.
 */
export function preparePastedNestedForUndo(nestedSprints, rootParentId) {
  const snapshots = (nestedSprints || []).map((ns) => ({
    ...ns,
    parentSprintId: ns.parentSprintId ?? rootParentId,
  }));
  const pastedIds = new Set(snapshots.map((s) => s.id));
  const rootIds = snapshots
    .filter((ns) => ns.id && !pastedIds.has(ns.parentSprintId))
    .map((ns) => ns.id);
  return { snapshots, rootIds };
}

/** Trash root nested campaigns created by paste (cascade covers descendants). */
export function trashPastedNestedRoots(rootIds, onTrashSprint) {
  if (!onTrashSprint || !rootIds?.length) return;
  rootIds.forEach((id) => {
    // Parent mission cards are removed by the paste undo itself — don't also
    // delete/unlink them from inside the trash handler.
    onTrashSprint(id, { preserveLinkedMission: true });
  });
}

/**
 * Restore nested campaigns after redo. Prefer onUpdateSprint so we clear
 * deletedAt on the existing trashed rows instead of creating duplicates.
 */
export function restorePastedNestedSprints(snapshots, rootParentId, { onUpdateSprint, onAddSprint } = {}) {
  if (!snapshots?.length) return;
  const sorted = sortNestedSprintsForPersistence(snapshots, rootParentId);
  for (const ns of sorted) {
    const clean = {
      ...ns,
      parentSprintId: ns.parentSprintId ?? rootParentId,
    };
    delete clean.deletedAt;
    delete clean.expiresAt;
    delete clean._trashedViaAncestorId;
    delete clean._originalParentSprintId;
    delete clean._originalParentTaskId;

    if (onUpdateSprint) {
      onUpdateSprint(clean);
    } else if (onAddSprint) {
      onAddSprint(clean);
    }
  }
}
